import {
    IAwardPointsRefundedEvent,
    IAwardPointsResetEvent,
    IRedeemPerkEvent,
    IRewardEarnedEvent,
    isAwardPointsRefundedEventLoose,
    isAwardPointsResetEventLoose,
    isRedeemPerkEventLoose,
    isRewardEarnedEventLoose
} from './perk.events.js';
import { IMemberAddedEvent, IMemberRemovedEvent, isMemberAddedEventLoose, isMemberRemovedEventLoose } from './segment.events.js';
import {
    isUserActivatedEventLoose,
    isUserActivationResentEventLoose,
    isUserChangedEventLoose,
    isUserCreatedEventLoose,
    isUserDeletedEventLoose,
    IUserActivatedEvent,
    IUserActivationResentEvent,
    IUserChangedEvent,
    IUserCreatedEvent,
    IUserDeletedEvent
} from './user.events.js';
import { IEvent, STRICT, LOOSE, BASIC, RICH } from './event.types.js';
import { EventIn } from './event.types.js';
import { enumify, isDefined, isString, TypeGuard } from '../lib/typeInference.js';
import {
    ICouponRedeemedEvent,
    ICouponRefundedEvent,
    IOrderCompletedEvent,
    IOrderCreatedEvent,
    IOrderItemsAddedEvent,
    IOrderPaymentReceivedEvent,
    IOrderPaymentRefundedEvent,
    IOrderUserBilledEvent,
    IOrderUserJoinedEvent,
    isCouponRedeemedEventLoose,
    isCouponRefundedEventLoose,
    isOrderCompletedEventLoose,
    isOrderCreatedEventLoose,
    isOrderItemsAddedEventLoose,
    isOrderPaymentReceivedEventLoose,
    isOrderPaymentRefundedEventLoose,
    isOrderUserBilledEventLoose,
    isOrderUserJoinedEventLoose
} from './order.events.js';
import { ICardCreatedEvent, ICardDeletedEvent, isCardCreatedEventLoose, isCardDeletedEventLoose } from './card.events.js';
import { ICheckinEvent, isCheckinEventLoose } from './checkin.events.js';
import { isUserCreditedEventLoose, IUserCreditedEvent } from './credit.events.js';
import { IReferalClaimedRefereeEvent, IReferalClaimedRefererEvent, isReferalClaimedRefereeEventLoose, isReferalClaimedRefererEventLoose } from './referal.events.js';
import { ICredentialVerifiedEvent, isCredentialVerifiedEventLoose } from './credential.events.js';
import { isVoucherRedeemedEventLoose, IVoucherRedeemedEvent } from './voucher.events.js';
import { IGiftCardActivatedEvent, IGiftCardTopupEvent, isGiftCardActivatedEventLoose, isGiftCardTopupEventLoose } from './giftCard.events.js';
import {
    IBookingCompletedEvent,
    IBookingCreatedEvent,
    IBookingChangedEvent,
    isBookingCompletedEventLoose,
    isBookingCreatedEventLoose,
    isBookingChangedEventLoose
} from './booking.events.js';

export type INoopEvent<CHECK extends STRICT | LOOSE = STRICT, DATA extends BASIC | RICH = BASIC> = IEvent<CHECK, DATA> & {
    readonly type: 'NOOP';
    metadata: {
        message: string;
    };
};

function isNoopEventLoose<DATA extends BASIC | RICH = BASIC>(x: EventIn<DATA>): x is INoopEvent<LOOSE, DATA> {
    return x.type === 'NOOP' && isDefined(x.metadata) && isString(x.metadata.message);
}

export const eventTypeValues = [
    'NOOP',
    'CARD_CREATED',
    'CARD_DELETED',
    'CHECKIN',
    'USER_CREDITED',
    // 'CAUSE_GIFT',
    'GIFT_CARD_TOPUP',
    'GIFT_CARD_ACTIVATED',
    'ORDER_CREATED',
    'ORDER_ITEMS_ADDED',
    'ORDER_USER_JOINED',
    'ORDER_PAYMENT_RECEIVED',
    'ORDER_PAYMENT_REFUNDED',
    'ORDER_COMPLETED',
    'ORDER_USER_BILLED',
    'REFERAL_CLAIMED_REFEREE',
    'REFERAL_CLAIMED_REFERER',
    // 'PURCHASE',
    'REWARD_EARNED',
    'REDEEM_PERK',
    'AWARD_POINTS_REFUNDED',
    'AWARD_POINTS_RESET',
    // 'REFUND',
    // 'SURVEY_RESPONDED',
    'MEMBER_ADDED',
    'MEMBER_REMOVED',
    'USER_CREATED',
    'USER_CHANGED',
    'USER_DELETED',
    'USER_ACTIVATION_RESENT',
    'USER_ACTIVATED',
    'CREDENTIAL_VERIFIED',
    'VOUCHER_REDEEMED',
    'COUPON_REDEEMED',
    'COUPON_REFUNDED',
    'BOOKING_CREATED',
    'BOOKING_CHANGED',
    'BOOKING_COMPLETED'
    // 'TEST_ACTION'
] as const;
export const EventTypeEnum = enumify(eventTypeValues);

export type EventType = (typeof eventTypeValues)[number];
export function isEventType(x: unknown): x is EventType {
    return eventTypeValues.includes(x as any);
}

export type EventTypeMap<CHECK extends STRICT | LOOSE = STRICT, DATA extends BASIC | RICH = BASIC> = {
    NOOP: INoopEvent<CHECK, DATA>;
    CARD_CREATED: ICardCreatedEvent<CHECK, DATA>;
    CARD_DELETED: ICardDeletedEvent<CHECK, DATA>;
    CHECKIN: ICheckinEvent<CHECK, DATA>;
    USER_CREDITED: IUserCreditedEvent<CHECK, DATA>;
    GIFT_CARD_TOPUP: IGiftCardTopupEvent<CHECK, DATA>;
    GIFT_CARD_ACTIVATED: IGiftCardActivatedEvent<CHECK, DATA>;
    ORDER_CREATED: IOrderCreatedEvent<CHECK, DATA>;
    ORDER_ITEMS_ADDED: IOrderItemsAddedEvent<CHECK, DATA>;
    ORDER_USER_JOINED: IOrderUserJoinedEvent<CHECK, DATA>;
    ORDER_PAYMENT_RECEIVED: IOrderPaymentReceivedEvent<CHECK, DATA>;
    ORDER_PAYMENT_REFUNDED: IOrderPaymentRefundedEvent<CHECK, DATA>;
    ORDER_COMPLETED: IOrderCompletedEvent<CHECK, DATA>;
    ORDER_USER_BILLED: IOrderUserBilledEvent<CHECK, DATA>;
    REFERAL_CLAIMED_REFEREE: IReferalClaimedRefereeEvent<CHECK, DATA>;
    REFERAL_CLAIMED_REFERER: IReferalClaimedRefererEvent<CHECK, DATA>;
    REWARD_EARNED: IRewardEarnedEvent<CHECK, DATA>;
    REDEEM_PERK: IRedeemPerkEvent<CHECK, DATA>;
    AWARD_POINTS_REFUNDED: IAwardPointsRefundedEvent<CHECK, DATA>;
    AWARD_POINTS_RESET: IAwardPointsResetEvent<CHECK, DATA>;
    MEMBER_ADDED: IMemberAddedEvent<CHECK, DATA>;
    MEMBER_REMOVED: IMemberRemovedEvent<CHECK, DATA>;
    USER_CREATED: IUserCreatedEvent<CHECK, DATA>;
    USER_CHANGED: IUserChangedEvent<CHECK, DATA>;
    USER_DELETED: IUserDeletedEvent<CHECK, DATA>;
    USER_ACTIVATION_RESENT: IUserActivationResentEvent<CHECK, DATA>;
    USER_ACTIVATED: IUserActivatedEvent<CHECK, DATA>;
    CREDENTIAL_VERIFIED: ICredentialVerifiedEvent<CHECK, DATA>;
    VOUCHER_REDEEMED: IVoucherRedeemedEvent<CHECK, DATA>;
    COUPON_REDEEMED: ICouponRedeemedEvent<CHECK, DATA>;
    COUPON_REFUNDED: ICouponRefundedEvent<CHECK, DATA>;
    BOOKING_CREATED: IBookingCreatedEvent<CHECK, DATA>;
    BOOKING_CHANGED: IBookingChangedEvent<CHECK, DATA>;
    BOOKING_COMPLETED: IBookingCompletedEvent<CHECK, DATA>;
};

export const eventTypeGuardMap: {
    [K in EventType]: TypeGuard<EventTypeMap<LOOSE>[K], EventIn>;
} = {
    NOOP: isNoopEventLoose,
    CARD_CREATED: isCardCreatedEventLoose,
    CARD_DELETED: isCardDeletedEventLoose,
    CHECKIN: isCheckinEventLoose,
    USER_CREDITED: isUserCreditedEventLoose,
    GIFT_CARD_TOPUP: isGiftCardTopupEventLoose,
    GIFT_CARD_ACTIVATED: isGiftCardActivatedEventLoose,
    ORDER_CREATED: isOrderCreatedEventLoose,
    ORDER_ITEMS_ADDED: isOrderItemsAddedEventLoose,
    ORDER_USER_JOINED: isOrderUserJoinedEventLoose,
    ORDER_PAYMENT_RECEIVED: isOrderPaymentReceivedEventLoose,
    ORDER_PAYMENT_REFUNDED: isOrderPaymentRefundedEventLoose,
    ORDER_COMPLETED: isOrderCompletedEventLoose,
    ORDER_USER_BILLED: isOrderUserBilledEventLoose,
    REFERAL_CLAIMED_REFEREE: isReferalClaimedRefereeEventLoose,
    REFERAL_CLAIMED_REFERER: isReferalClaimedRefererEventLoose,
    REWARD_EARNED: isRewardEarnedEventLoose,
    REDEEM_PERK: isRedeemPerkEventLoose,
    AWARD_POINTS_REFUNDED: isAwardPointsRefundedEventLoose,
    AWARD_POINTS_RESET: isAwardPointsResetEventLoose,
    MEMBER_ADDED: isMemberAddedEventLoose,
    MEMBER_REMOVED: isMemberRemovedEventLoose,
    USER_CREATED: isUserCreatedEventLoose,
    USER_CHANGED: isUserChangedEventLoose,
    USER_DELETED: isUserDeletedEventLoose,
    USER_ACTIVATION_RESENT: isUserActivationResentEventLoose,
    USER_ACTIVATED: isUserActivatedEventLoose,
    CREDENTIAL_VERIFIED: isCredentialVerifiedEventLoose,
    VOUCHER_REDEEMED: isVoucherRedeemedEventLoose,
    COUPON_REDEEMED: isCouponRedeemedEventLoose,
    COUPON_REFUNDED: isCouponRefundedEventLoose,
    BOOKING_CREATED: isBookingCreatedEventLoose,
    BOOKING_CHANGED: isBookingChangedEventLoose,
    BOOKING_COMPLETED: isBookingCompletedEventLoose
};

type EventTypeFunctionMap<R, DATA extends BASIC | RICH = BASIC> = {
    [K in EventType]?: (event: EventTypeMap<LOOSE, DATA>[K]) => R;
};

export function switchEventType<DATA extends BASIC | RICH = BASIC>(x: EventIn<DATA>, handlers: EventTypeFunctionMap<Promise<void>, DATA>): Promise<void> {
    const type = x.type as EventType;
    const guard = eventTypeGuardMap[type];
    if (typeof guard === 'function' && guard(x)) {
        const handler = handlers[type];
        if (typeof handler === 'function') {
            return handler(x as any);
        }
    }
    return Promise.resolve();
}
export function switchEventTypeNP<U>(x: EventIn, handlers: EventTypeFunctionMap<U>): U | undefined {
    const type = x.type as EventType;
    const guard = eventTypeGuardMap[type];
    if (typeof guard === 'function' && guard(x)) {
        const handler = handlers[type];
        if (typeof handler === 'function') {
            return handler(x as any);
        }
    }
    return;
}
